import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import * as Sentry from '@sentry/browser';
import { useContext, useEffect } from 'react';

import { HotelContext } from 'app/context/HotelContext/HotelContext';
import type { User } from 'app/typings';
import type { ChannelManager } from 'app/typings/channelManagers';
import type {
  TrackingEvent,
  TrackingEventProperties,
} from 'app/typings/track/tracking';

import type { Flag } from '../featureFlags/featureFlags';
import { CONTROL_GROUP, TREATMENT_GROUP } from '../featureFlags/featureFlags';
import { getExperimentGroup } from '../featureFlags/featureFlagsService';

let isInitialized = false;
let currentHotelId: number | undefined = undefined;
let currentIsLegacy: boolean | undefined = undefined;
let currentChannelManager: ChannelManager | undefined = undefined;

type HotelTrackingContext = {
  hotelId: number | undefined;
  isLegacy: boolean;
  channelManager: ChannelManager | undefined;
};

export const amplitudeSetHotel = ({
  hotelId,
  isLegacy,
  channelManager,
}: HotelTrackingContext) => {
  currentHotelId = hotelId;
  currentIsLegacy = isLegacy;
  currentChannelManager = channelManager;
};

export const amplitudeInit = async (user: User, amplitudeApiKey?: string) => {
  if (!amplitudeApiKey) {
    console.warn('Amplitude API key is not provided. Skipping initialization.');

    return;
  }

  const sessionReplayTracking = sessionReplayPlugin({
    sampleRate: 1,
  });

  try {
    await amplitude.init(amplitudeApiKey, undefined, {
      defaultTracking: false,
    });
    amplitude.add(sessionReplayTracking);
    isInitialized = true;
    await amplitudeSetUser(user);
  } catch (error: unknown) {
    Sentry.captureException(error);
    console.error('Error initializing Amplitude:', error);
  }
};

export const amplitudeSetUser = async (user: User) => {
  if (!isInitialized) {
    return;
  }

  try {
    const identifyObj = new amplitude.Identify();

    identifyObj.set('user_id', `${user.id}`);
    identifyObj.set('id', user.id);
    identifyObj.set('locale', user.locale);
    identifyObj.set('hasFacebook', user.hasFacebook);
    identifyObj.set('optinNewsletter', user.optinNewsletter);
    identifyObj.set('createdAt', user.createdAt);
    identifyObj.set('clubId', user.clubId);
    identifyObj.set('role', user.role);

    await amplitude.identify(identifyObj);
    amplitude.setUserId(`${user.id}`);
  } catch (error: unknown) {
    Sentry.captureException(error);
    console.error('Error setting user in Amplitude:', error);
  }
};

export const amplitudeTrack = async <T extends TrackingEvent>(
  event: T,
  properties?: TrackingEventProperties[T]
) => {
  if (!isInitialized) {
    return;
  }

  const referrer = document.referrer === '' ? 'direct' : document.referrer;

  const eventProperties = {
    ...properties,
    previousPage: window.history?.state?.previous ?? referrer,
    sourceApp: 'web',
    hotelId: currentHotelId,
    isLegacy: currentIsLegacy,
    channelManager: currentChannelManager,
  };

  try {
    await amplitude.track(event, eventProperties);
  } catch (error: unknown) {
    Sentry.captureException(error);
  }
};

export const isControlGroupByKey = (key: Flag) => {
  return getExperimentGroup(key) === CONTROL_GROUP;
};

export const isTreatmentGroup = (key: Flag) => {
  return getExperimentGroup(key) === TREATMENT_GROUP;
};

type ViewPageProperty = {
  page: string;
};

export const useTrackViewPage = (params: ViewPageProperty) => {
  const { hotelInfo } = useContext(HotelContext);

  useEffect(() => {
    amplitude.track('View Page', params);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want this to run one time
  }, [hotelInfo]);

  return null;
};
